// ============================================================
// ColonieLinkFamilyModal.js
// Manual link/unlink to a school family (reference only)
// ============================================================

import React, { useState } from 'react';
import { X, Search, Link, Link2Off, Loader } from 'lucide-react';

const ColonieLinkFamilyModal = ({ registration, onClose, onSave, apiCall, showError }) => {
  const [q, setQ]           = useState('');
  const [results, setRes]   = useState([]);
  const [loading, setLd]    = useState(false);
  const [saving, setSaving] = useState(false);

  const search = async () => {
    if (q.length < 2) return;
    setLd(true);
    try { setRes(await apiCall(`/colonie/search-families?q=${encodeURIComponent(q)}`)); }
    catch (err) { showError(err.message); }
    finally { setLd(false); }
  };

  const link = async (familyId) => {
    setSaving(true);
    try {
      await apiCall(`/colonie/registrations/${registration.RegistrationID}/link-family`, {
        method: 'PUT',
        body: JSON.stringify({ familyId }),
      });
      onSave(familyId);
    } catch (err) { showError(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h2 className="font-bold text-gray-800 flex items-center gap-2"><Link size={17}/>Link to School Family</h2>
            <p className="text-xs text-gray-500 mt-0.5">{registration.ChildName} — reference only, no financial impact</p>
          </div>
          <button onClick={onClose}><X size={17} className="text-gray-400"/></button>
        </div>

        <div className="p-5 space-y-4">
          {registration.FamilyID && (
            <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-xl p-3">
              <span className="text-sm text-blue-800">Linked: <strong>{registration.FamilyID}</strong></span>
              <button onClick={() => link(null)} disabled={saving}
                className="flex items-center gap-1 text-xs text-red-600 font-semibold hover:text-red-800">
                <Link2Off size={13}/>Unlink
              </button>
            </div>
          )}

          <p className="text-xs text-gray-500">
            Search by family name, phone, or Family ID. The link is for display only — it does not affect school balances.
          </p>

          <div className="flex gap-2">
            <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && search()}
              placeholder="Name, phone, or Family ID…"
              className="flex-1 border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-400"/>
            <button onClick={search} disabled={loading}
              className="px-4 bg-blue-600 text-white rounded-xl disabled:opacity-50">
              {loading ? <Loader size={14} className="animate-spin"/> : <Search size={14}/>}
            </button>
          </div>

          {results.length > 0 && (
            <div className="border rounded-xl divide-y overflow-hidden">
              {results.map(f => (
                <div key={f.FamilyID} className="flex items-center justify-between p-3 hover:bg-gray-50">
                  <div>
                    <p className="font-semibold text-sm text-gray-800">{f.FatherName} / {f.MotherName}</p>
                    <p className="text-xs text-gray-400">{f.FamilyID} · {f.PrimaryPhone}</p>
                  </div>
                  <button onClick={() => link(f.FamilyID)} disabled={saving}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 disabled:opacity-50">
                    Link
                  </button>
                </div>
              ))}
            </div>
          )}
          {results.length === 0 && q.length >= 2 && !loading && (
            <p className="text-sm text-gray-400 text-center py-2">No matching school families found.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ColonieLinkFamilyModal;
